import React, { type PropsWithChildren, useCallback, useContext } from 'react';

import { connect } from 'react-redux';

import Colors from 'common/colors/constants';
import BackgroundColorContainer from 'common/containers/BackgroundColorContainer';
import BingContainer from 'common/containers/BingContainer';
import CannyContainer from 'common/containers/CannyContainer';
import CannylyticsContainer from 'common/containers/CannylyticsContainer';
import HubSpotContainer from 'common/containers/HubSpotContainer';
import IntercomContainer from 'common/containers/IntercomContainer';
import LinkedInContainer from 'common/containers/LinkedInContainer';
import MixpanelContainer from 'common/containers/MixpanelContainer';
import ModalContainer from 'common/containers/ModalContainer';
import PostLinkContainer from 'common/containers/PostLinkContainer';
import TintColorContainer, { DefaultTintColor } from 'common/containers/TintColorContainer';
import ToastContainer from 'common/containers/ToastContainer';
import IsAdminViewContext from 'common/contexts/IsAdminViewContext';
import Helmet from 'common/helmets/Helmet';
import SpinnerV2 from 'common/SpinnerV2';
import AdminV2ErrorView from 'common/subdomain/admin/v2/AdminV2ErrorView';
import devURL from 'common/util/devURL';

import EventContainer from './EventContainer';
import { LocationContext, RouterContext } from './RouterContainer';

import type { Company } from 'common/api/endpoints/companies';
import type { Viewer } from 'common/api/endpoints/viewer';
import type { Post } from 'common/api/resources/posts';
import type { State } from 'redux-connect';

type withReducerProps<T> = Omit<T, 'loading'> & {
  error: string | null;
  loading: boolean;
};

type Props = {
  viewer: withReducerProps<Viewer>;
  company: withReducerProps<Company & { notFound: boolean }>;
  params: { boardURLName: string; postURLName: string };
};

const AdminContainer = ({ viewer, company, children, params }: PropsWithChildren<Props>) => {
  const router = useContext(RouterContext);
  const location = useContext(LocationContext);

  const handleAdminRedirect = useCallback(() => {
    const { pathname } = location;
    const { boardURLName, postURLName } = params;

    const homeRegex = /^\/admin\/boards\/?$/;
    const feedbackRegex = /^\/admin\/feedback\/[a-z0-9-]+\/p\/[a-z0-9-]+\/?$/;
    const boardRegex = /^\/admin\/board\/[a-z0-9-]+\/?$/;
    const postRegex = /^\/admin\/board\/[a-z0-9-]+\/p\/[a-z0-9-]+\/?$/;
    const createRegex = /^\/admin\/board\/[a-z0-9-]+\/create\/?$/;
    const votersRegex = /^\/admin\/board\/[a-z0-9-]+\/p\/[a-z0-9-]+\/voters\/?$/;

    if (homeRegex.test(pathname)) {
      router.replace('/');
      return true;
    } else if (boardRegex.test(pathname)) {
      router.replace(`/${boardURLName}`);
      return true;
    } else if (postRegex.test(pathname)) {
      router.replace(`/${boardURLName}/p/${postURLName}`);
      return true;
    } else if (createRegex.test(pathname)) {
      router.replace(`/${boardURLName}/create`);
      return true;
    } else if (votersRegex.test(pathname)) {
      router.replace(`/${boardURLName}/p/${postURLName}/voters`);
      return true;
    } else if (feedbackRegex.test(pathname)) {
      router.replace(`/${boardURLName}/p/${postURLName}`);
      return true;
    } else if (viewer.loggedOut) {
      // Only redirect to login if no other pattern matched and user is logged out
      if (typeof window !== 'undefined') {
        const url = devURL('https://canny.io/login');
        window.location.assign(url);
      }
      return true;
    }

    return false; // No redirect pattern matched
  }, [location, params, router, viewer.loggedOut]);

  if (viewer.loading || company.loading) {
    return (
      <Container>
        <SpinnerV2 />
      </Container>
    );
  }

  if (company.notFound) {
    return <Container>There is no such company. Did you enter the right URL?</Container>;
  }

  if (viewer.error || company.error) {
    return (
      <Container>
        <AdminV2ErrorView />
      </Container>
    );
  }

  // Handle logged out users - always redirect to login
  if (viewer.loggedOut) {
    handleAdminRedirect();
    return null; // Prevent flash by returning immediately
  }

  // Handle non-members - redirect to public routes or show error
  if (!company.viewerIsMember) {
    const didRedirect = handleAdminRedirect();
    if (!didRedirect) {
      // No redirect pattern matched, so show error
      return <Container>You aren't a member of this company.</Container>;
    }
    return null; // Redirect happened, prevent flash
  }

  type Container = React.ComponentType<any>;
  const containers: { container: Container; props?: Record<string, any> }[] = [
    { container: EventContainer },
    { container: HubSpotContainer },
    { container: BingContainer },
    { container: CannylyticsContainer },
    { container: MixpanelContainer, props: { property: 'admin', trackPageViews: true } },
    { container: LinkedInContainer },
    { container: IntercomContainer },
    { container: CannyContainer },
    { container: ModalContainer },
    { container: ToastContainer },
    { container: BackgroundColorContainer, props: { backgroundColor: Colors.white } },
    { container: TintColorContainer, props: { tintColor: DefaultTintColor } },
    { container: PostLinkContainer, props: { getPostLink } },
    { container: IsAdminViewContext.Provider, props: { value: true } },
  ];

  const result = containers.reduce((acc, containerItem) => {
    const Container = containerItem.container;
    const props = containerItem.props ?? {};
    return <Container {...props}>{acc}</Container>;
  }, children);

  return (
    <>
      <Helmet allowIndexing={false} />
      {result}
    </>
  );
};

function getPostLink(post: Post): string {
  return `/admin/feedback/${post.board.urlName}/p/${post.urlName}?boards=${post.board.urlName}`;
}

const Container = ({ children }: { children: React.ReactNode }) => {
  return <div className="flex h-screen w-screen items-center justify-center p-6">{children}</div>;
};

export default connect((state: State) => ({ viewer: state.viewer, company: state.company }))(
  AdminContainer,
);
